import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, Wrench, Store, ArrowRight, Loader2, Check } from "lucide-react";
import { api, humanizeAxiosError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../lib/constants";

const ROLES = [
  { id: "cliente", label: "Cliente", Icon: HardHat, color: "#1E3A8A", desc: "Busco maestros, contratistas o materiales para mis proyectos." },
  { id: "contratista", label: "Maestro-Contratista", Icon: Wrench, color: "#7E22CE", desc: "Ofrezco mis servicios profesionales y recibo solicitudes." },
  { id: "ferreteria", label: "Ferretería", Icon: Store, color: "#D97706", desc: "Vendo materiales y respondo pedidos de construcción." },
];

export default function OnboardingRol() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function confirmar() {
    if (!sel) return;
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/onboarding/rol", { rol: sel });
      await refresh();
      navigate("/", { replace: true });
    } catch (e) {
      setError(humanizeAxiosError(e));
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-5 py-10" data-testid="onboarding-page">
      <img src={LOGO_URL} alt="ConstruLink" className="h-14 w-auto max-w-[240px] object-contain mb-8" />
      <div className="w-full max-w-3xl text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red">Bienvenido a la Familia</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">¿Cómo usarás ConstruLink?</h1>
        <p className="mt-2 text-slate-500">Elige tu rol para personalizar tu experiencia. Podrás ajustarlo luego en tu perfil.</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {ROLES.map((r) => {
            const active = sel === r.id;
            const Icon = r.Icon;
            return (
              <button
                key={r.id}
                onClick={() => setSel(r.id)}
                data-testid={`onboarding-rol-${r.id}`}
                className={`relative text-left bg-white rounded-2xl border-2 p-5 transition-all ${active ? "shadow-lifted -translate-y-1" : "border-slate-100 shadow-soft hover:border-slate-200"}`}
                style={active ? { borderColor: r.color } : {}}
              >
                {active && (
                  <span className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: r.color }}>
                    <Check size={14} />
                  </span>
                )}
                <span className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: `${r.color}1a`, color: r.color }}>
                  <Icon size={24} />
                </span>
                <h3 className="text-lg font-bold text-slate-900">{r.label}</h3>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">{r.desc}</p>
              </button>
            );
          })}
        </div>

        {error && <p className="mt-4 text-sm text-brand-red" data-testid="onboarding-error">{error}</p>}

        <button
          onClick={confirmar}
          disabled={!sel || loading}
          data-testid="onboarding-continuar"
          className="mt-8 inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-hover disabled:opacity-50 text-white font-semibold px-8 py-3 rounded-xl transition-colors shadow-sm"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          {loading ? "Guardando…" : (<>Continuar al Ecosistema <ArrowRight size={16} /></>)}
        </button>
      </div>
    </div>
  );
}
